import { Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FnCell, MetaLoadingCell, ShiftCell, StatusDot } from './StatusCells'
import type { CashTerminal } from './types'
import { formatPing, isUkm5, terminalLabel } from './utils'
import styles from '@/pages/CashPage.module.css'

interface Props {
  title?: string
  rows: CashTerminal[]
  archiveTab: boolean
  onOpen: (t: CashTerminal) => void
  onCopyIp: (ip?: string) => void
}

export function CashTable({ title, rows, archiveTab, onOpen, onCopyIp }: Props) {
  const online = rows.filter((t) => t.status === 'online').length
  const offline = rows.length - online

  const columns: ColumnsType<CashTerminal> = [
    {
      title: 'Касса',
      width: 96,
      render: (_, r) => <span className={styles.num}>{terminalLabel(r)}</span>,
    },
    {
      title: 'Тип',
      width: 72,
      render: (_, r) => (
        <span className={styles.typeChip}>{isUkm5(r.type) ? 'УКМ‑5' : 'УКМ‑4'}</span>
      ),
    },
    {
      title: 'IP',
      width: 128,
      render: (_, r) => (
        <Tooltip title="Скопировать">
          <button
            type="button"
            className={styles.ipBtn}
            onClick={(e) => {
              e.stopPropagation()
              onCopyIp(r.ip)
            }}
          >
            {r.ip || '—'}
          </button>
        </Tooltip>
      ),
    },
    ...(archiveTab
      ? [
          {
            title: 'Архив',
            ellipsis: true,
            render: (_: unknown, r: CashTerminal) => (
              <span className={styles.muted}>
                {r.archivedAt ? new Date(r.archivedAt).toLocaleString('ru-RU') : '—'}
                {r.archivedBy ? ` · ${r.archivedBy}` : ''}
              </span>
            ),
          } as ColumnsType<CashTerminal>[number],
        ]
      : [
          {
            title: 'Статус',
            width: 104,
            render: (_: unknown, r: CashTerminal) => (
              <StatusDot online={r.status === 'online'} />
            ),
          } as ColumnsType<CashTerminal>[number],
          {
            title: 'Смена',
            ellipsis: true,
            render: (_: unknown, r: CashTerminal) =>
              r.kktFnPending ? <MetaLoadingCell /> : <ShiftCell label={r.kktStatus} />,
          } as ColumnsType<CashTerminal>[number],
          {
            title: 'ФН',
            ellipsis: true,
            render: (_: unknown, r: CashTerminal) =>
              r.kktFnPending ? <MetaLoadingCell /> : <FnCell label={r.fnStatus} />,
          } as ColumnsType<CashTerminal>[number],
          {
            title: 'Пинг',
            width: 72,
            render: (_: unknown, r: CashTerminal) => (
              <span className={styles.ping}>{formatPing(r.ms)}</span>
            ),
          } as ColumnsType<CashTerminal>[number],
        ]),
  ]

  return (
    <section className={styles.column}>
      {title ? (
        <div className={styles.columnHead}>
          <h2 className={styles.columnTitle}>{title}</h2>
          <div className={styles.badges}>
            <span className={styles.badgeTotal}>{rows.length}</span>
            {!archiveTab ? (
              <>
                <span className={styles.badgeOn}>{online}</span>
                {offline > 0 ? <span className={styles.badgeOff}>{offline}</span> : null}
              </>
            ) : null}
          </div>
        </div>
      ) : null}
      <Table
        size="small"
        pagination={false}
        rowKey={(r) => String(r.id)}
        dataSource={rows}
        columns={columns}
        locale={{ emptyText: archiveTab ? 'Архив пуст' : 'Нет касс' }}
        className={styles.dataTable}
        tableLayout="fixed"
        onRow={(record) => ({
          onClick: () => {
            if (!archiveTab) onOpen(record)
          },
          className: record.status !== 'online' && !archiveTab ? styles.rowOffline : undefined,
        })}
        scroll={{ x: 640 }}
      />
    </section>
  )
}
